import {
    ExtensionOutlined,
    AutoAwesome,
    DescriptionOutlined,
    CheckCircle,
} from '@mui/icons-material';
import {
    Box,
    Drawer,
    Paper,
    Card,
    Typography,
    useTheme,
    alpha,
    Divider,
} from '@mui/material';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useUnifiedAccountState } from '../../hooks/useUnifiedAccountState';

interface FlowStep {
    id: string;
    path: string;
    titleKey: string;
    icon: JSX.Element;
}

interface FlowNavigationProps {
    isOpen?: boolean;
    onClose?: () => void;
}

const FLOW_STEPS: FlowStep[] = [
    {
        id: 'import',
        path: '/integrations',
        titleKey: 'flow.steps.dataImport',
        icon: <ExtensionOutlined />,
    },
    {
        id: 'check',
        path: '/smart-check',
        titleKey: 'flow.steps.dataCheck',
        icon: <AutoAwesome />,
    },
    {
        id: 'report',
        path: '/tax-reports',
        titleKey: 'flow.steps.taxReport',
        icon: <DescriptionOutlined />,
    },
];

const FlowNavigation: React.FC<FlowNavigationProps> = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    
    // Check if integrations exist
    const accountState = useUnifiedAccountState({ skipIssueFetch: true });
    const hasIntegrations = accountState.hasIntegrations;

    // Memoize Smart Check context to prevent flickering
    const isSmartCheckContext = useMemo(() => {
        const result = searchParams.get('smartCheckContext') === 'true' && location.pathname === '/transactions';
        console.log('[FlowNavigation] Smart Check Context:', result, 'Path:', location.pathname, 'Params:', searchParams.toString());
        return result;
    }, [searchParams, location.pathname]);
    
    const getCurrentStepIndex = (): number => {
        // If in Smart Check context, consider Smart Check as the active step for progress
        if (isSmartCheckContext) {
            const smartCheckIndex = FLOW_STEPS.findIndex(step => step.path === '/smart-check');
            return smartCheckIndex >= 0 ? smartCheckIndex : 0;
        }
        const currentStep = FLOW_STEPS.findIndex(step => step.path === location.pathname);
        return currentStep >= 0 ? currentStep : 0;
    };

    const currentStepIndex = getCurrentStepIndex();

    const handleStepClick = (step: FlowStep) => {
        // Check if step should be disabled
        const isStepDisabled = !hasIntegrations && (step.path === '/smart-check' || step.path === '/tax-reports');
        
        // Don't navigate if step is disabled
        if (isStepDisabled) {
            return;
        }
        
        // Preserve smartCheckContext param when navigating if we're in that context
        if (isSmartCheckContext && step.path !== '/smart-check') {
            const params = new URLSearchParams(searchParams);
            navigate(`${step.path}?${params.toString()}`);
        } else {
            navigate(step.path);
        }
    };

    // Mobile navigation content (horizontal top bar)
    const mobileNavigationContent = (
        <Paper
            elevation={0}
            sx={{
                position: 'fixed',
                top: { xs: 64, sm: 72 },
                left: 0,
                right: 0,
                zIndex: (theme) => theme.zIndex.drawer,
                bgcolor: (theme) => theme.palette.background.paper,
                borderBottom: '1px solid',
                borderColor: 'divider',
                px: { xs: 1, sm: 2 },
                py: 1.5,
            }}
        >
            {/* Horizontal Steps */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative' }}>
                {/* Horizontal progress line */}
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '15%',
                        right: '15%',
                        height: 4,
                        bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.3)' : alpha(theme.palette.divider, 0.6),
                        borderRadius: 2,
                        zIndex: 0,
                        transform: 'translateY(-50%)',
                        '&:after': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            height: '100%',
                            width: `${(currentStepIndex / (FLOW_STEPS.length - 1)) * 100}%`,
                            bgcolor: (theme) => theme.palette.primary.main,
                            borderRadius: 2,
                            transition: 'width 0.4s ease-out',
                        }
                    }}
                />

                {FLOW_STEPS.map((step, index) => {
                    const isActive = location.pathname === step.path;
                    const isSmartCheckStep = step.path === '/smart-check';
                    const isActiveInContext = isSmartCheckContext && isSmartCheckStep;
                    const isCompleted = index < currentStepIndex;
                    const isDisabled = !hasIntegrations && (step.path === '/smart-check' || step.path === '/tax-reports');

                    return (
                        <Box
                            key={step.id}
                            onClick={() => handleStepClick(step)}
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                cursor: isDisabled ? 'not-allowed' : 'pointer',
                                flex: 1,
                                position: 'relative',
                                zIndex: 1,
                                opacity: isDisabled ? 0.4 : 1,
                            }}
                        >
                            {/* Step Circle */}
                            <Box
                                sx={{
                                    width: { xs: 28, sm: 32 },
                                    height: { xs: 28, sm: 32 },
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    border: `2px solid`,
                                    borderColor: isDisabled
                                        ? theme.palette.action.disabled
                                        : isActive || isActiveInContext
                                        ? theme.palette.primary.main
                                        : isCompleted
                                        ? theme.palette.success.main
                                        : theme.palette.mode === 'dark' 
                                        ? 'rgba(255, 255, 255, 0.5)' 
                                        : alpha(theme.palette.divider, 0.5),
                                    bgcolor: isDisabled
                                        ? theme.palette.action.disabledBackground
                                        : isActive || isActiveInContext
                                        ? theme.palette.primary.main
                                        : isCompleted
                                        ? theme.palette.success.main
                                        : theme.palette.background.paper,
                                    color: isDisabled
                                        ? theme.palette.action.disabled
                                        : isActive || isActiveInContext || isCompleted
                                        ? theme.palette.primary.contrastText
                                        : theme.palette.text.secondary,
                                    mb: 1,
                                    // Add pulsing effect for Smart Check when in context
                                    ...(isActiveInContext && {
                                        animation: 'pulse 2s infinite',
                                        '@keyframes pulse': {
                                            '0%': { boxShadow: `0 0 0 0 ${alpha(theme.palette.primary.main, 0.7)}` },
                                            '70%': { boxShadow: `0 0 0 6px ${alpha(theme.palette.primary.main, 0)}` },
                                            '100%': { boxShadow: `0 0 0 0 ${alpha(theme.palette.primary.main, 0)}` },
                                        },
                                    }),
                                }}
                            >
                                {isCompleted ? (
                                    <CheckCircle sx={{ fontSize: '1rem' }} />
                                ) : (
                                    React.cloneElement(step.icon, {
                                        sx: { fontSize: { xs: '0.8rem', sm: '0.9rem' } }
                                    })
                                )}
                            </Box>

                            {/* Step Label */}
                            <Typography
                                variant="caption"
                                sx={{
                                    fontSize: { xs: '0.65rem', sm: '0.75rem' },
                                    fontWeight: isActive || isActiveInContext ? 600 : 500,
                                    color: isDisabled
                                        ? theme.palette.action.disabled
                                        : isActive || isActiveInContext
                                        ? theme.palette.primary.main
                                        : isCompleted
                                        ? theme.palette.success.main
                                        : theme.palette.text.secondary,
                                    textAlign: 'center',
                                    lineHeight: 1.2,
                                }}
                            >
                                {t(step.titleKey)}
                            </Typography>
                        </Box>
                    );
                })}
            </Box>
        </Paper>
    );

    // Desktop box navigation content
    const desktopBoxContent = (
        <Card
            elevation={1}
            sx={{
                position: 'fixed',
                top: { xs: 130, md: 140 },
                left: { xs: '20px', sm: '80px', md: '80px' }, // Align with header content area
                width: { xs: '90%', sm: 260, md: 280 },
                maxWidth: { xs: 280 },
                zIndex: (theme) => theme.zIndex.drawer,
                bgcolor: (theme) => theme.palette.mode === 'dark' 
                    ? theme.palette.background.paper
                    : 'rgb(249, 247, 245)', // Match header background
                p: 4,
                borderRadius: 2,
                border: '1px solid transparent',
                transition: 'all 0.2s ease',
                '&:hover': {
                    transform: 'translateY(-1px)',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                },
            }}
        >
            <Box>
                {/* Section Title */}
                <Typography 
                    variant="caption" 
                    sx={{ 
                        display: 'block',
                        mb: 2,
                        px: 2,
                        color: 'text.secondary',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '0.08em'
                    }}
                >
                    {t('flow.navigation.steps', 'SCHRITTE')}
                </Typography>
                
                {/* Simple Step List */}
                <Box>
                    {FLOW_STEPS.map((step, index) => {
                        const isActive = location.pathname === step.path;
                        const isSmartCheckStep = step.path === '/smart-check';
                        const isActiveInContext = isSmartCheckContext && isSmartCheckStep;
                        const isDisabled = !hasIntegrations && (step.path === '/smart-check' || step.path === '/tax-reports');

                        return (
                            <Box
                                key={step.id}
                                onClick={() => handleStepClick(step)}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 2,
                                    mb: 1.25,
                                    py: 2,
                                    px: { xs: 1.5, sm: 2 },
                                    borderRadius: 1.5,
                                    cursor: isDisabled ? 'not-allowed' : 'pointer',
                                    transition: 'all 0.2s ease',
                                    opacity: isDisabled ? 0.4 : 1,
                                    bgcolor: (isActive || isActiveInContext) 
                                        ? 'rgba(255, 200, 150, 0.25)' // Soft light orange/peach for active
                                        : 'transparent',
                                    '&:hover': {
                                        ...(isDisabled ? {} : {
                                            bgcolor: (isActive || isActiveInContext)
                                                ? 'rgba(255, 200, 150, 0.35)'
                                                : (theme) => theme.palette.mode === 'dark' 
                                                ? 'rgba(255, 255, 255, 0.05)'
                                                : 'rgba(0, 0, 0, 0.05)',
                                            transform: 'translateY(-1px)',
                                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                                        }),
                                    },
                                }}
                            >
                                {/* Simple Icon */}
                                <Box
                                    sx={{
                                        color: (isActive || isActiveInContext)
                                            ? '#D35400' // Darker orange for active
                                            : (theme) => theme.palette.mode === 'dark' 
                                            ? 'rgba(255, 255, 255, 0.7)'
                                            : 'rgba(0, 0, 0, 0.45)', // Lighter gray for non-active
                                        display: 'flex',
                                        alignItems: 'center',
                                    }}
                                >
                                    {React.cloneElement(step.icon, {
                                        sx: { fontSize: '1.25rem' }
                                    })}
                                </Box>

                                {/* Step Text */}
                                <Typography
                                    sx={{
                                        fontSize: { xs: '0.85rem', sm: '0.95rem' },
                                        fontWeight: (isActive || isActiveInContext) ? 700 : 600,
                                        color: (isActive || isActiveInContext)
                                            ? '#D35400' // Darker orange for active
                                            : (theme) => theme.palette.mode === 'dark' 
                                            ? 'rgba(255, 255, 255, 0.8)'
                                            : 'rgba(0, 0, 0, 0.6)', // Lighter gray for non-active
                                    }}
                                >
                                    {t(step.titleKey)}
                                </Typography>
                            </Box>
                        );
                    })}
                </Box>
            </Box>
        </Card>
    );

    // Create simple mobile navigation bar
    const mobileSimpleNavigation = (
        <Paper
            elevation={0}
            sx={{
                position: 'fixed',
                top: 72, // More space below header
                left: 0,
                right: 0,
                zIndex: (theme) => theme.zIndex.drawer,
                bgcolor: (theme) => theme.palette.mode === 'dark' 
                    ? theme.palette.background.paper
                    : 'rgb(249, 247, 245)', // Match header background
                borderBottom: '1px solid transparent',
                py: 1,
                px: 2,
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                {FLOW_STEPS.map((step, index) => {
                    const isActive = location.pathname === step.path;
                    const isSmartCheckStep = step.path === '/smart-check';
                    const isActiveInContext = isSmartCheckContext && isSmartCheckStep;
                    const isDisabled = !hasIntegrations && (step.path === '/smart-check' || step.path === '/tax-reports');

                    return (
                        <Box
                            key={step.id}
                            onClick={() => handleStepClick(step)}
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                cursor: isDisabled ? 'not-allowed' : 'pointer',
                                flex: 1,
                                py: 1,
                                px: 0.5,
                                borderRadius: 1,
                                opacity: isDisabled ? 0.4 : 1,
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                    ...(isDisabled ? {} : {
                                        bgcolor: (theme) => theme.palette.mode === 'dark' 
                                            ? 'rgba(255, 255, 255, 0.05)'
                                            : 'rgba(0, 0, 0, 0.05)',
                                    }),
                                },
                            }}
                        >
                            {/* Simple Icon */}
                            <Box
                                sx={{
                                    color: isDisabled
                                        ? (theme) => theme.palette.action.disabled
                                        : (isActive || isActiveInContext)
                                        ? '#D35400'
                                        : (theme) => theme.palette.mode === 'dark' 
                                        ? 'rgba(255, 255, 255, 0.7)'
                                        : 'rgba(0, 0, 0, 0.45)',
                                    mb: 0.5,
                                }}
                            >
                                {React.cloneElement(step.icon, {
                                    sx: { fontSize: '1.1rem' }
                                })}
                            </Box>

                            {/* Simple Label */}
                            <Typography
                                variant="caption"
                                sx={{
                                    fontSize: '0.7rem',
                                    fontWeight: (isActive || isActiveInContext) ? 600 : 500,
                                    color: isDisabled
                                        ? (theme) => theme.palette.action.disabled
                                        : (isActive || isActiveInContext)
                                        ? '#D35400'
                                        : (theme) => theme.palette.mode === 'dark' 
                                        ? 'rgba(255, 255, 255, 0.8)'
                                        : 'rgba(0, 0, 0, 0.6)',
                                    textAlign: 'center',
                                    lineHeight: 1.2,
                                }}
                            >
                                {t(step.titleKey)}
                            </Typography>
                        </Box>
                    );
                })}
            </Box>
        </Paper>
    );

    return desktopBoxContent;
};

export default FlowNavigation;